import React, {
    useCallback, useContext, useState, useMemo, useEffect
} from 'react';
import { HashLink as Link } from 'react-router-hash-link';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import classNames from 'classnames';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import PropTypes from 'prop-types';
import { TranslatorContext } from '@jutro/locale';
import { SgScrollUtils, useSgTrackPageName } from 'saga-modules';
import { sanitizeUrl } from '@jutro/platform';
import { SgFaqSectionComponent, CepConstants } from 'saga-cep-specific-components';
import CommonConstants from 'sg-common-constants';
import SgCepSideMenu from 'sg-cep-side-menu';
// eslint-disable-next-line import/no-unresolved
import faqConfig from 'faq-config';
import metadata from './FaqPage.metadata.json5';
import styles from './FaqPage.module.scss';

import messages from './FaqPage.messages';

function FaqHorizontalMenu(props) {
    const translator = useContext(TranslatorContext);

    const { value, enabled } = props;
    const { id, title } = value;

    const horizontalMenuLinkStyle = classNames(styles.wizardLi, {
        [styles.wizardLiDisabled]: !enabled
    });
    return (
        <li key={id} className={`sgFaqSectionLink_${translator(title)} ${horizontalMenuLinkStyle}`}>
            <Link to={`/faq#${id}`} scroll={(el) => SgScrollUtils.scrollToElementById(el.id)}>{translator(title)}</Link>
        </li>
    );
}

function checkSafeMatching(phrase = '', query = '') {
    const htmlStrippedPhrase = _.unescape(phrase).replace(/<[^<>]+>/gi, '');
    return htmlStrippedPhrase.toLocaleLowerCase().includes(query.toLowerCase());
}

const searchTopic = (topic, query, translator) => {
    const questionMatching = checkSafeMatching(
        translator(_.find(messages, { id: topic.question })),
        query
    );
    const answerMatching = checkSafeMatching(
        _.reduce(topic.answers, (acc, value) => acc + translator(_.find(messages, { id: value.message })), ''),
        query
    );

    return questionMatching || answerMatching;
};

const filterTopics = (topics, query, translator) => {
    return topics.filter((topic) => searchTopic(topic, query, translator));
};

const getFilteredSections = (sections, query, translator) => {
    return sections
        .map((section) => {
            return { ...section, topics: filterTopics(section.topics, query, translator) };
        })
        .filter((section) => {
            return !_.isEmpty(section.topics);
        });
};

function FaqPage() {
    const translator = useContext(TranslatorContext);
    const [formData, setFormData] = useState({ searchTopic: '' });

    const history = useHistory();

    const handleRedirect = useCallback((event) => {
        event.preventDefault();
        if (CommonConstants.SAGA_OUTSIDE_CEP_PATHS.some((path) => event.target.href.includes(path))) {
            const target = event.target.href.includes('tel:') ? '_self' : '_blank';
            window.open(sanitizeUrl(event.target.href), target);
        } else {
            history.push(event.target.pathname);
        }
    }, [history]);

    useSgTrackPageName(CepConstants.PAGE_NAMES.FAQ);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const anchors = document.getElementById('faqMainContent').getElementsByTagName('a');

        _.each(anchors, (anchor) => {
            anchor.addEventListener('click', handleRedirect);
        });

        return () => {
            _.each(anchors, (anchor) => {
                anchor.removeEventListener('click', handleRedirect);
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData]);

    const filteredSections = useMemo(() => {
        return getFilteredSections(faqConfig.sections, formData.searchTopic, translator);
    }, [formData.searchTopic, translator]);

    const readValue = useCallback(
        (id, path = '') => {
            if (id === 'searchTopicField') {
                return _.get(formData, path);
            }

            if (path && path.includes('filteredSections')) {
                const sectionPath = path.replace('filteredSections', 'sections');
                const newFaqObject = { sections: filteredSections };
                return _.get(newFaqObject, sectionPath);
            }

            return _.get(faqConfig, path);
        },
        [filteredSections, formData]
    );

    const writeValue = useCallback(
        (value, path) => {
            const formDataClone = _.cloneDeep(formData);
            _.set(formDataClone, path, value);
            setFormData(formDataClone);
        },
        [formData, setFormData]
    );

    const generatedOverrides = useCallback(() => {
        const searchTopicText = _.get(formData, 'searchTopic');

        const sectionOverrides = faqConfig.sections.map((section, index) => {
            const linkEnabled = filteredSections.some((item) => item.id === section.id);

            return {
                [`faqSectionContainer${index}`]: {
                    searchText: searchTopicText,
                    messages: messages
                },
                [`faqHorizontalMenuSectionPageTitle${index}`]: { enabled: linkEnabled }
            };
        });

        return Object.assign({}, ...sectionOverrides);
    }, [filteredSections, formData]);

    const overrideProps = {
        '@field': {
            onValueChange: writeValue
        },
        ...generatedOverrides()
    };

    const resolvers = {
        resolveValue: readValue,
        resolveComponentMap: {
            FaqHorizontalMenu: FaqHorizontalMenu,
            SgFaqSectionComponent: SgFaqSectionComponent,
            SgCepSideMenu
        },
        resolveClassNameMap: styles
    };

    return <div>{renderContentFromMetadata(metadata.pageContent, overrideProps, resolvers)}</div>;
}

FaqHorizontalMenu.propTypes = {
    value: PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.shape({
            id: PropTypes.string.isRequired,
            defaultMessage: PropTypes.string.isRequired
        }).isRequired
    }).isRequired,
    enabled: PropTypes.bool.isRequired
};

export default FaqPage;
